import { useState } from "react";
import { Menu, Search, Bell, RefreshCw, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

interface TopbarProps {
  section: string;
  pageTitle: string;
  onMenu: () => void;
  onSearch: (q: string) => void;
  onQuickAction: () => void;
  lastSync: Date | null;
  notifications: any[];
  unreadCount: number;
  showNotifications: boolean;
  onToggleNotifications: () => void;
  onMarkAllRead: () => void;
  onNotificationClick: (n: any) => void;
}

export default function Topbar({
  section, pageTitle, onMenu, onSearch, onQuickAction, lastSync,
  notifications, unreadCount, showNotifications, onToggleNotifications,
  onMarkAllRead, onNotificationClick,
}: TopbarProps) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");

  const syncLabel = (() => {
    if (!lastSync) return null;
    const mins = Math.floor((Date.now() - lastSync.getTime()) / 60000);
    if (mins < 1) return t("common.sync_now");
    return t("common.sync_min", { count: mins });
  })();

  const timeAgo = (created: string) => {
    const mins = Math.floor((Date.now() - new Date(created).getTime()) / 60000);
    if (mins < 1) return t("common.akkurat_na");
    if (mins < 60) return t("common.min_siden", { count: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t("common.t_siden", { count: hrs });
    return t("common.d_siden", { count: Math.floor(hrs / 24) });
  };

  return (
    <header className="h-[56px] shrink-0 bg-white/90 backdrop-blur-md border-b border-line flex items-center justify-between px-4 sm:px-6 gap-3 relative z-30">
      {/* Venstre: meny + breadcrumb */}
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={onMenu} className="lg:hidden p-2 -ml-2 rounded-[9px] text-muted hover:bg-black/[0.05]" title={t("nav.meny")}>
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 text-[13.5px] min-w-0">
          <span className="text-faint hidden sm:inline">{section}</span>
          <span className="text-faint hidden sm:inline">/</span>
          <span className="font-semibold text-night truncate">{pageTitle}</span>
        </div>
      </div>

      {/* Midt: søk */}
      <div className="relative hidden md:block flex-1 max-w-md">
        <Search className="w-3.5 h-3.5 text-faint absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") onSearch(q); }}
          placeholder={t("common.sok_placeholder")}
          className="cx-search"
        />
      </div>

      {/* Høyre: sync + varsler + quick action */}
      <div className="flex items-center gap-2 sm:gap-3">
        {syncLabel && (
          <span className="hidden lg:flex items-center gap-1.5 text-[12px] text-faint">
            <RefreshCw className="w-3 h-3" /> {syncLabel}
          </span>
        )}
        <div className="relative">
          <button onClick={onToggleNotifications} className="relative p-2 rounded-[9px] text-muted hover:text-night hover:bg-black/[0.04] transition-colors">
            <Bell className="w-[18px] h-[18px]" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-neg rounded-full ring-[1.5px] ring-white flex items-center justify-center text-[9px] font-bold text-white px-1">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
          {showNotifications && (
            <>
              <div className="fixed inset-0 z-40" onClick={onToggleNotifications} />
              <div className="absolute right-0 top-11 w-[calc(100vw-32px)] sm:w-96 max-w-[24rem] bg-card rounded-[14px] shadow-pop border border-line z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-line flex items-center justify-between">
                  <p className="text-[14px] font-bold text-night">{t("common.varsler")}</p>
                  {unreadCount > 0 && (
                    <button onClick={onMarkAllRead} className="text-[11px] text-info font-semibold hover:underline">
                      {t("common.marker_alle_lest")}
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="text-[13px] text-faint text-center py-8">{t("common.ingen_varsler")}</p>
                  ) : notifications.map((n: any, i: number) => (
                    <div key={n.id} onClick={() => onNotificationClick(n)}
                      className={`px-4 py-3 flex gap-3 cursor-pointer hover:bg-black/[0.03] transition-colors ${i > 0 ? "border-t border-line" : ""} ${!n.is_read ? "bg-info/[0.04]" : ""}`}>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] ${!n.is_read ? "font-bold text-night" : "text-night"}`}>{n.title}</p>
                        {n.message && <p className="text-[12px] text-muted truncate mt-0.5">{n.message}</p>}
                        <p className="text-[11px] text-faint mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                      {!n.is_read && <div className="w-2 h-2 bg-info rounded-full shrink-0 mt-1.5" />}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <button onClick={onQuickAction} className="cx-btn-dark">
          <Plus className="w-4 h-4" /> <span className="hidden sm:inline">{t("common.hurtighandling")}</span>
        </button>
      </div>
    </header>
  );
}
